import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { DataGrid, GridToolbar, GridActionsCellItem } from '@mui/x-data-grid';
import { Add, Delete, Edit, Refresh, LockReset, Security } from '@mui/icons-material';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog';
import { fetchJson } from '../../../utils/fetchJson';
import { UserContext } from '../../../contexts/UserContext';

const ROLE_OPTIONS = [
  { value: 'SystemAdmin', label: 'System Admin' },
  { value: 'ITManager', label: 'IT Manager' },
  { value: 'SalesRep', label: 'Sales Rep' },
  { value: 'SupportAgent', label: 'Support Agent' }
];

export default function SystemUsersTab() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({ role: '', status: '', search: '' });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'ITManager', status: 'active' });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Permissions dialog
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);

  const SYSTEM_PAGES = [
    { key: 'system-overview', label: t('systemUsers.pages.overview', 'Overview / Dashboard') },
    { key: 'schools', label: t('systemUsers.pages.schools', 'School Directory') },
    { key: 'registration-queue', label: t('systemUsers.pages.registrationQueue', 'Registration Queue') },
    { key: 'onboarding-status', label: t('systemUsers.pages.onboarding', 'Onboarding Status') },
    { key: 'billing', label: t('systemUsers.pages.billing', 'Billing & Subscriptions') },
    { key: 'users-access', label: t('systemUsers.pages.usersAccess', 'Users & Access') },
    { key: 'system-settings', label: t('systemUsers.pages.settings', 'Platform Settings') },
    { key: 'communications', label: t('systemUsers.pages.communications', 'Communications') },
    { key: 'security', label: t('systemUsers.pages.security', 'Security & Audit') },
    { key: 'support-tickets', label: t('systemUsers.pages.supportTickets', 'Support Tickets') },
    { key: 'integrations', label: t('systemUsers.pages.integrations', 'Integrations') },
    { key: 'it-services', label: t('systemUsers.pages.itServices', 'IT Services') }
  ];

  const canManage = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('users-access');
  }, [user]);

  const openPermissions = async (row) => {
    if (!canManage) return;
    setPermUser(row);
    try {
      const data = await fetchJson(`/api/system-users/${row.id}/permissions`);
      setUserPermissions(Array.isArray(data?.permissions) ? data.permissions : []);
    } catch (_) {
      setUserPermissions([]);
    }
    setPermDialogOpen(true);
  };

  const togglePermission = (pageKey) => {
    if (!canManage) return;
    setUserPermissions(prev =>
      prev.includes(pageKey) ? prev.filter(p => p !== pageKey) : [...prev, pageKey]
    );
  };

  const savePermissions = async () => {
    if (!canManage) return;
    try {
      await fetchJson(`/api/system-users/${permUser.id}/permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions: userPermissions })
      });
      setSnack({ open: true, severity: 'success', message: t('systemUsers.permissionsUpdated', 'Permissions updated') });
      setPermDialogOpen(false);
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e?.message || t('systemUsers.permissionsFailed', 'Failed to save permissions') });
    }
  };

  const openCreate = () => {
    if (!canManage) return;
    setEditing(null);
    setForm({ name: '', email: '', phone: '', role: 'ITManager', status: 'active' });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    if (!canManage) return;
    setEditing(row);
    setForm({
      name: row?.name || '',
      email: row?.email || '',
      phone: row?.phone || '',
      role: row?.role || 'ITManager',
      status: row?.status || 'active'
    });
    setDialogOpen(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(page + 1));
      params.set('pageSize', String(pageSize));
      if (filters.role) params.set('role', filters.role);
      if (filters.status) params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);

      const data = await fetchJson(`/api/system-users?${params.toString()}`);
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(Number.isFinite(data?.total) ? data.total : 0);
    } catch (e) {
      setError(e?.message || t('systemUsers.loadFailed', 'Failed to load system users'));
    } finally {
      setLoading(false);
    }
  }, [filters.role, filters.search, filters.status, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!canManage) return;
    setError('');
    try {
      if (!form.name) throw new Error('Name is required');
      if (!form.email) throw new Error('Email is required');
      if (!form.role) throw new Error('Role is required');

      if (editing?.id) {
        await fetchJson(`/api/system-users/${editing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            phone: form.phone || null,
            role: form.role,
            status: form.status
          })
        });
        setSnack({ open: true, severity: 'success', message: t('systemUsers.updated', 'System user updated') });
      } else {
        await fetchJson('/api/system-users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            phone: form.phone || null,
            role: form.role,
            status: form.status
          })
        });
        setSnack({ open: true, severity: 'success', message: t('systemUsers.created', 'System user created') });
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      setError(e?.message || t('systemUsers.saveFailed', 'Failed to save system user'));
    }
  };

  const setStatus = async (row, status) => {
    if (!canManage) return;
    try {
      await fetchJson(`/api/system-users/${row.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      setSnack({ open: true, severity: 'success', message: t('systemUsers.statusUpdated', 'Status updated') });
      await load();
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e?.message || t('systemUsers.statusFailed', 'Failed to update status') });
    }
  };

  const resetPassword = async (row) => {
    if (!canManage) return;
    try {
      await fetchJson(`/api/system-users/${row.id}/reset-password`, { method: 'POST' });
      setSnack({ open: true, severity: 'success', message: t('systemUsers.passwordReset', 'Password reset email sent (if email configured)') });
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e?.message || t('systemUsers.passwordResetFailed', 'Failed to reset password') });
    }
  };

  const askDelete = (row) => {
    if (!canManage) return;
    setDeleteTarget(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    if (!canManage) return;
    try {
      await fetchJson(`/api/system-users/${deleteTarget.id}`, { method: 'DELETE' });
      setSnack({ open: true, severity: 'success', message: t('systemUsers.deleted', 'System user deleted') });
      setDeleteOpen(false);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e?.message || t('systemUsers.deleteFailed', 'Failed to delete system user') });
      setDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  const columns = useMemo(() => {
    return [
      { field: 'name', headerName: t('systemUsers.name', 'Name'), flex: 1, minWidth: 150 },
      { field: 'email', headerName: t('systemUsers.email', 'Email'), flex: 1, minWidth: 200 },
      { field: 'phone', headerName: t('systemUsers.phone', 'Phone'), width: 130 },
      { field: 'role', headerName: t('systemUsers.role', 'Role'), width: 140,
        valueFormatter: (params) => {
          const opt = ROLE_OPTIONS.find(r => r.value === params.value);
          return opt ? opt.label : params.value;
        }
      },
      { field: 'status', headerName: t('systemUsers.status', 'Status'), width: 110 },
      {
        field: 'lastLoginAt', headerName: t('systemUsers.lastLogin', 'Last Login'), width: 170,
        valueGetter: (params) => params?.row?.lastLoginAt ? new Date(params.row.lastLoginAt).toLocaleString() : t('systemUsers.never', 'Never')
      },
      {
        field: 'actions',
        type: 'actions',
        width: 140,
        getActions: (params) => {
          const row = params.row;
          const actions = [
            <GridActionsCellItem key="edit" icon={<Edit />} label={t('systemUsers.edit', 'Edit')} onClick={() => openEdit(row)} disabled={!canManage} />,
            <GridActionsCellItem key="perms" icon={<Security />} label={t('systemUsers.permissions', 'Permissions')} onClick={() => openPermissions(row)} disabled={!canManage} />,
            <GridActionsCellItem key="reset" icon={<LockReset />} label={t('systemUsers.resetPassword', 'Reset Password')} onClick={() => resetPassword(row)} showInMenu disabled={!canManage} />,
            <GridActionsCellItem key="delete" icon={<Delete />} label={t('systemUsers.delete', 'Delete')} onClick={() => askDelete(row)} showInMenu disabled={!canManage} />
          ];
          if (row?.status === 'active') {
            actions.unshift(
              <GridActionsCellItem key="deactivate" icon={<Delete />} label={t('systemUsers.deactivate', 'Deactivate')} onClick={() => setStatus(row, 'inactive')} showInMenu disabled={!canManage} />
            );
          } else {
            actions.unshift(
              <GridActionsCellItem key="activate" icon={<Add />} label={t('systemUsers.activate', 'Activate')} onClick={() => setStatus(row, 'active')} showInMenu disabled={!canManage} />
            );
          }
          return actions;
        }
      }
    ];
  }, [canManage, t]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">{t('systemUsers.title', 'System Users')}</Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<Refresh />} variant="outlined" onClick={load}>{t('systemUsers.refresh', 'Refresh')}</Button>
          <Button startIcon={<Add />} variant="contained" onClick={openCreate} disabled={!canManage}>{t('systemUsers.addUser', 'Add System User')}</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="sys-user-role">{t('systemUsers.role', 'Role')}</InputLabel>
              <Select
                labelId="sys-user-role"
                label={t('systemUsers.role', 'Role')}
                value={filters.role}
                onChange={(e) => { setPage(0); setFilters(f => ({ ...f, role: e.target.value })); }}
              >
                <MenuItem value=""><em>{t('systemUsers.all', 'All')}</em></MenuItem>
                {ROLE_OPTIONS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="sys-user-status">{t('systemUsers.status', 'Status')}</InputLabel>
              <Select
                labelId="sys-user-status"
                label={t('systemUsers.status', 'Status')}
                value={filters.status}
                onChange={(e) => { setPage(0); setFilters(f => ({ ...f, status: e.target.value })); }}
              >
                <MenuItem value=""><em>{t('systemUsers.all', 'All')}</em></MenuItem>
                <MenuItem value="active">{t('systemUsers.active', 'Active')}</MenuItem>
                <MenuItem value="inactive">{t('systemUsers.inactive', 'Inactive')}</MenuItem>
                <MenuItem value="suspended">{t('systemUsers.suspended', 'Suspended')}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              label={t('systemUsers.search', 'Search')}
              value={filters.search}
              onChange={(e) => { setPage(0); setFilters(f => ({ ...f, search: e.target.value })); }}
              sx={{ minWidth: 260 }}
            />
          </Stack>
        </CardContent>
      </Card>

      <div style={{ height: 520, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowCount={total}
          pagination
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(s) => { setPage(0); setPageSize(s); }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          components={{ Toolbar: GridToolbar }}
          disableSelectionOnClick
        />
      </div>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? t('systemUsers.editUser', 'Edit System User') : t('systemUsers.addUser', 'Add System User')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={t('systemUsers.name', 'Name')} value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField label={t('systemUsers.email', 'Email')} value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} fullWidth />
            <TextField label={t('systemUsers.phone', 'Phone')} value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} fullWidth />

            <FormControl fullWidth>
              <InputLabel id="sys-user-role-form">{t('systemUsers.role', 'Role')}</InputLabel>
              <Select
                labelId="sys-user-role-form"
                label={t('systemUsers.role', 'Role')}
                value={form.role}
                onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
              >
                {ROLE_OPTIONS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="sys-user-status-form">{t('systemUsers.status', 'Status')}</InputLabel>
              <Select
                labelId="sys-user-status-form"
                label={t('systemUsers.status', 'Status')}
                value={form.status}
                onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
              >
                <MenuItem value="active">{t('systemUsers.active', 'Active')}</MenuItem>
                <MenuItem value="inactive">{t('systemUsers.inactive', 'Inactive')}</MenuItem>
                <MenuItem value="suspended">{t('systemUsers.suspended', 'Suspended')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('systemUsers.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={save} disabled={!canManage}>{t('systemUsers.save', 'Save')}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={deleteOpen}
        onClose={() => { setDeleteOpen(false); setDeleteTarget(null); }}
        onConfirm={confirmDelete}
        title={t('systemUsers.deleteTitle', 'Delete System User')}
        message={deleteTarget ? t('systemUsers.deleteConfirm', 'Delete {{name}} ({{email}})?', { name: deleteTarget.name, email: deleteTarget.email }) : t('systemUsers.deleteUser', 'Delete this user?')}
      />

      {/* Permissions Dialog */}
      <Dialog open={permDialogOpen} onClose={() => setPermDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('systemUsers.permissionsTitle', 'Permissions')} — {permUser?.name || ''} ({permUser?.role || ''})</DialogTitle>
        <DialogContent>
          {permUser?.role === 'SystemAdmin' ? (
            <Alert severity="info" sx={{ mb: 2 }}>{t('systemUsers.adminAllPerms', 'System Admin has all permissions by default.')}</Alert>
          ) : (
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t('systemUsers.selectPages', 'Select which system pages this user can access.')}
            </Typography>
          )}
          <FormGroup>
            {SYSTEM_PAGES.map(pg => (
              <FormControlLabel
                key={pg.key}
                control={
                  <Checkbox
                    checked={permUser?.role === 'SystemAdmin' || userPermissions.includes(pg.key)}
                    onChange={() => togglePermission(pg.key)}
                    disabled={permUser?.role === 'SystemAdmin'}
                  />
                }
                label={pg.label}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermDialogOpen(false)}>{t('systemUsers.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={savePermissions} disabled={permUser?.role === 'SystemAdmin' || !canManage}>{t('systemUsers.savePermissions', 'Save Permissions')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
